import { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { useNotification } from '@/components/notifications';
import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/select';
import { LoadingSpinner } from '@/components/loading-spinner';
import { addMcpServer } from '@/services/workflow/add-mcp-server';
import { MCPServerConfig } from './mcp-servers-input';

interface McpServerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (server: MCPServerConfig) => void;
  /** If provided, modal opens in edit mode with pre-filled data */
  initialData?: MCPServerConfig;
}

type CustomHeader = {
  id: string;
  name: string;
  value: string;
};

type McpServerFormState = {
  url: string;
  label: string;
  description: string;
  authentication: {
    type: 'none' | 'api_key' | 'custom_headers';
    api_key?: string;
    header_name?: string;
    headers?: CustomHeader[];
  };
};

const createHeaderId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `header_${Math.random().toString(36).slice(2)}`;

const headersRecordToList = (
  headers?: Record<string, string>,
): CustomHeader[] =>
  Object.entries(headers || {}).map(([name, value]) => ({
    id: createHeaderId(),
    name,
    value,
  }));

const headersListToRecord = (headers: CustomHeader[]): Record<string, string> =>
  Object.fromEntries(
    headers
      .filter(
        (header) => header.name.trim() !== '' && header.value.trim() !== '',
      )
      .map((header) => [header.name.trim(), header.value]),
  );

const getInitialFormState = (data?: MCPServerConfig): McpServerFormState => {
  if (!data) {
    return {
      url: '',
      label: '',
      description: '',
      authentication: { type: 'none' },
    };
  }

  return {
    url: data.url,
    label: data.label,
    description: data.description || '',
    authentication:
      data.authentication.type === 'custom_headers'
        ? {
            ...data.authentication,
            headers: headersRecordToList(data.authentication.headers),
          }
        : { ...data.authentication, headers: undefined },
  };
};

export const McpServerModal: React.FC<McpServerModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}) => {
  const notification = useNotification();
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<McpServerFormState>(() =>
    getInitialFormState(initialData),
  );

  const isEditMode = !!initialData;

  // Reset form when modal opens or initialData changes
  useEffect(() => {
    if (isOpen) {
      setForm(getInitialFormState(initialData));
    }
  }, [isOpen, initialData]);

  const handleClose = () => {
    setForm(getInitialFormState());
    onClose();
  };

  const handleAuthTypeChange = (
    authType: 'none' | 'api_key' | 'custom_headers',
  ) => {
    setForm((prev) => ({
      ...prev,
      authentication: (() => {
        switch (authType) {
          case 'none':
            return { type: 'none' };
          case 'api_key':
            return {
              type: 'api_key',
              api_key: '',
              header_name: 'Authorization',
            };
          case 'custom_headers':
            return {
              type: 'custom_headers',
              headers: [],
            };
        }
      })(),
    }));
  };

  const updateCustomHeader = (
    id: string,
    field: 'name' | 'value',
    fieldValue: string,
  ) => {
    setForm((prev) => {
      if (prev.authentication.type !== 'custom_headers') return prev;
      const headers = (prev.authentication.headers || []).map((header) =>
        header.id === id ? { ...header, [field]: fieldValue } : header,
      );
      return {
        ...prev,
        authentication: {
          ...prev.authentication,
          headers,
        },
      };
    });
  };

  const removeCustomHeader = (id: string) => {
    setForm((prev) => {
      if (prev.authentication.type !== 'custom_headers') return prev;
      const headers = (prev.authentication.headers || []).filter(
        (header) => header.id !== id,
      );
      return {
        ...prev,
        authentication: {
          ...prev.authentication,
          headers,
        },
      };
    });
  };

  const addCustomHeader = () => {
    setForm((prev) => {
      if (prev.authentication.type !== 'custom_headers') return prev;
      const headers = [
        ...(prev.authentication.headers || []),
        { id: createHeaderId(), name: '', value: '' },
      ];
      return {
        ...prev,
        authentication: {
          ...prev.authentication,
          headers,
        },
      };
    });
  };

  const handleSave = async () => {
    // Validate required fields
    if (!form.url.trim() || !form.label.trim()) {
      notification.error('URL and Label are required fields');
      return;
    }

    // Validate authentication fields based on type
    if (form.authentication.type === 'api_key') {
      if (!form.authentication.api_key?.trim()) {
        notification.error('API Key is required for API Key authentication');
        return;
      }
      if (!form.authentication.header_name?.trim()) {
        notification.error(
          'Header Name is required for API Key authentication',
        );
        return;
      }
    }

    if (form.authentication.type === 'custom_headers') {
      const namedHeaders = (form.authentication.headers || []).filter(
        (header) => header.name.trim() !== '' && header.value.trim() !== '',
      );
      const uniqueNames = new Set(
        namedHeaders.map((header) => header.name.trim()),
      );
      if (uniqueNames.size !== namedHeaders.length) {
        notification.error('Custom header names must be unique');
        return;
      }
      if (uniqueNames.size === 0) {
        notification.error(
          'At least one header with both name and value is required for Custom Headers authentication',
        );
        return;
      }
    }

    // Validate URL format
    try {
      new URL(form.url);
    } catch {
      notification.error('Invalid URL format');
      return;
    }

    // Build the server config
    const serverConfig: MCPServerConfig = {
      url: form.url.trim(),
      label: form.label.trim(),
      description: form.description.trim() || undefined,
      authentication: (() => {
        switch (form.authentication.type) {
          case 'none':
            return { type: 'none' as const };
          case 'api_key':
            return {
              type: 'api_key' as const,
              api_key: form.authentication.api_key?.trim() || '',
              header_name:
                form.authentication.header_name?.trim() || 'Authorization',
            };
          case 'custom_headers':
            return {
              type: 'custom_headers' as const,
              headers: headersListToRecord(form.authentication.headers || []),
            };
        }
      })(),
    };

    // Edit mode: update locally without API call
    if (isEditMode) {
      onSave(serverConfig);
      handleClose();
      notification.success('MCP server updated successfully');
      return;
    }

    // Add mode: call the API first
    setIsSaving(true);
    try {
      const response = await addMcpServer(serverConfig);

      if (response.success) {
        onSave(serverConfig);
        handleClose();
        notification.success(
          response.message || 'MCP server added successfully',
        );
      } else {
        notification.error(
          response.message || response.detail || 'Failed to add MCP server',
        );
      }
    } catch (error) {
      console.error('Error adding MCP server:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred';
      notification.error(`Failed to add MCP server: ${errorMessage}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isDialogOpen={isOpen}
      setIsDialogOpen={(open) => {
        if (!open) handleClose();
      }}
      title={<span>{isEditMode ? 'Edit' : 'Add'} MCP Server</span>}
      footer={
        <div className='flex gap-2'>
          <Button variant='outline' onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <div className='flex items-center gap-2'>
                <LoadingSpinner size='sm' />
                <span>Saving...</span>
              </div>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      }
    >
      <div className='flex flex-col gap-4 mt-4 max-h-[70vh] overflow-y-auto'>
        <div className='space-y-2'>
          <Label htmlFor='mcp-url' className='label-required'>
            URL
          </Label>
          <Input
            id='mcp-url'
            value={form.url}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                url: e.target.value,
              }))
            }
            placeholder='https://mcp.example.com'
            className='w-full'
          />
        </div>

        <div className='space-y-2'>
          <Label htmlFor='mcp-label' className='label-required'>
            Label
          </Label>
          <Input
            id='mcp-label'
            value={form.label}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                label: e.target.value,
              }))
            }
            placeholder='example_server'
            className='w-full'
          />
        </div>

        <div className='space-y-2'>
          <Label htmlFor='mcp-description'>Description</Label>
          <Textarea
            id='mcp-description'
            value={form.description}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
            placeholder='Optional description of the MCP server'
            className='w-full'
            rows={3}
          />
        </div>

        <div className='space-y-2'>
          <Label htmlFor='mcp-auth-type'>Authentication Type</Label>
          <Select
            options={[
              'NoAuthentication',
              'APIKeyAuthentication',
              'CustomHeadersAuthentication',
            ]}
            selected={
              form.authentication.type === 'none'
                ? 'NoAuthentication'
                : form.authentication.type === 'api_key'
                  ? 'APIKeyAuthentication'
                  : 'CustomHeadersAuthentication'
            }
            onSelect={(value) => {
              if (value === 'NoAuthentication') {
                handleAuthTypeChange('none');
              } else if (value === 'APIKeyAuthentication') {
                handleAuthTypeChange('api_key');
              } else if (value === 'CustomHeadersAuthentication') {
                handleAuthTypeChange('custom_headers');
              }
            }}
            allowAllOption={false}
            className='w-full'
          />
        </div>

        {/* API Key Authentication Fields */}
        {form.authentication.type === 'api_key' && (
          <div className='space-y-3 pl-4 border-l-2'>
            <div className='space-y-2'>
              <Label htmlFor='mcp-api-key' className='label-required'>
                API Key
              </Label>
              <Input
                id='mcp-api-key'
                type='password'
                value={form.authentication.api_key || ''}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    authentication: {
                      ...prev.authentication,
                      api_key: e.target.value,
                    },
                  }))
                }
                placeholder='sk-1234567890abcdef'
                className='w-full'
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='mcp-header-name' className='label-required'>
                Header Name
              </Label>
              <Input
                id='mcp-header-name'
                value={form.authentication.header_name || 'Authorization'}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    authentication: {
                      ...prev.authentication,
                      header_name: e.target.value,
                    },
                  }))
                }
                placeholder='Authorization'
                className='w-full'
              />
            </div>
          </div>
        )}

        {/* Custom Headers Authentication Fields */}
        {form.authentication.type === 'custom_headers' && (
          <div className='space-y-3 pl-4 border-l-2'>
            <div className='flex items-center justify-between'>
              <Label className='label-required'>Custom Headers</Label>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={addCustomHeader}
                className='h-7'
              >
                <Plus className='h-3 w-3 mr-1' />
                Add Header
              </Button>
            </div>
            {(form.authentication.headers || []).map((header) => (
              <div key={header.id} className='flex gap-2 items-center'>
                <Input
                  value={header.name}
                  onChange={(e) =>
                    updateCustomHeader(header.id, 'name', e.target.value)
                  }
                  placeholder='Header name (e.g., X-API-Key)'
                  className='flex-1'
                />
                <Input
                  value={header.value}
                  onChange={(e) =>
                    updateCustomHeader(header.id, 'value', e.target.value)
                  }
                  placeholder='Header value'
                  className='flex-1'
                />
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  onClick={() => removeCustomHeader(header.id)}
                  className='h-9 w-9 text-destructive hover:text-destructive'
                >
                  <X className='h-4 w-4' />
                </Button>
              </div>
            ))}
            {(!form.authentication.headers ||
              form.authentication.headers.length === 0) && (
              <p className='text-sm text-muted-foreground'>
                No headers added. Click &quot;Add Header&quot; to add one.
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};
